import type { IntlShape } from 'react-intl';

import type { ReviewCardCopy } from '../types/review-types';

/** Copy for a card with nothing to show; nothing renders it. */
export const EMPTY_REVIEW_CARD_COPY: ReviewCardCopy = {
  title: '',
  positionLabel: '',
  previousLabel: '',
  nextLabel: '',
  closeLabel: '',
  intro: '',
  reviewIntro: '',
  confirmLabel: '',
  buildNewLabel: '',
  closedHeadline: '',
  closedBody: '',
  reopenLabel: '',
};

interface ReviewCardCopyArgs {
  /** 0-based index of the current page; the item count on the review screen. */
  stepIndex: number;
  itemCount: number;
  questionCount: number;
  matchCount: number;
}

/** Every string the Review card shows, for the turn's current step. */
export const buildReviewCardCopy = (
  { formatMessage, formatList }: IntlShape,
  { stepIndex, itemCount, questionCount, matchCount }: ReviewCardCopyArgs
): ReviewCardCopy => {
  const title = formatMessage({ id: 'epiChat.review.title', defaultMessage: 'Review' });

  const openParts = [
    ...(questionCount > 0
      ? [
          formatMessage(
            {
              id: 'epiChat.review.openQuestions',
              defaultMessage: '{count, plural, one {# question} other {# questions}}',
            },
            { count: questionCount }
          ),
        ]
      : []),
    ...(matchCount > 0
      ? [
          formatMessage(
            {
              id: 'epiChat.review.openMatches',
              defaultMessage: '{count, plural, one {# variable match} other {# variable matches}}',
            },
            { count: matchCount }
          ),
        ]
      : []),
  ];

  return {
    title,
    // The review screen is the card's last page, so it counts: one selection is
    // "1 of 2", and the review screen that follows it is "2 of 2".
    positionLabel: formatMessage(
      { id: 'epiChat.review.position', defaultMessage: '{current} of {total}' },
      { current: stepIndex + 1, total: itemCount + 1 }
    ),
    previousLabel: formatMessage({
      id: 'epiChat.review.previous',
      defaultMessage: 'Previous item',
    }),
    nextLabel: formatMessage({ id: 'epiChat.review.next', defaultMessage: 'Next item' }),
    closeLabel: formatMessage({
      id: 'epiChat.review.close',
      defaultMessage: 'Close — reply in the box below',
    }),
    intro: formatMessage(
      {
        id: 'epiChat.review.intro',
        defaultMessage:
          '{kinds, select, both {Before I continue, I have a couple of questions and some variable matches for you to review.} questions {Before I continue, I have {count, plural, one {a question} other {a few questions}} for you.} other {Here’s what I found in your library for the {count, plural, one {variable} other {# variables}} you asked for. Pick one for each — nothing is saved until you confirm.}}',
      },
      {
        kinds: questionCount > 0 ? (matchCount > 0 ? 'both' : 'questions') : 'matches',
        count: questionCount > 0 ? questionCount : matchCount,
      }
    ),
    reviewIntro: formatMessage({
      id: 'epiChat.review.reviewIntro',
      defaultMessage:
        'Here’s everything you picked. Confirm to continue, or change anything first.',
    }),
    confirmLabel: formatMessage({ id: 'epiChat.review.confirm', defaultMessage: 'Confirm' }),
    buildNewLabel: formatMessage({
      id: 'epiChat.variableMatch.buildNew',
      defaultMessage: 'Build a new variable',
    }),
    closedHeadline: formatMessage({ id: 'epiChat.review.skipped', defaultMessage: 'Skipped' }),
    closedBody: formatMessage(
      {
        id: 'epiChat.review.closedBody',
        defaultMessage:
          'Dismissed without answering, so nothing here was applied. {open} still open.',
      },
      { open: formatList(openParts, { type: 'conjunction' }) }
    ),
    reopenLabel: formatMessage({ id: 'epiChat.review.reopen', defaultMessage: 'Reopen' }),
  };
};
